/* eslint-disable camelcase */

import EzController from 'controllers/ez-base-controller';
import { parseTemplate, parseBoolean } from 'lib/helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { CONTRIBUTOR_CLICK, CONTENT_SELECTION_CLICK } from 'components/analytics/conversions/constants/actions';
import { ajaxGet } from 'lib/ajax-helpers';
import MediaMatcher from 'lib/media-matcher';

const mediaMatcher = new MediaMatcher();

export default class ThumbnailContribInfoController extends EzController {
  static targets = [
    'contribInfoTemplate',
    'missingAvatarSvg',
    'contribThumbnails',
    'idCollection',
  ];

  connect() {
    if (this.hasContribInfoTemplateTarget && this.contributorsIds.length) this.setupMediaQueries();
    if (this.hasContribThumbnailsTarget) this.setupFollowingThumbnails();

    mediaMatcher.evaluateAll();
    mediaMatcher.addListeners();

    window.addEventListener('setNewThumbnailContributorInfo', this.setNewThumbnailInfo);
  }

  disconnect() {
    mediaMatcher.removeListeners();
    mediaMatcher.clearQueries();

    window.removeEventListener('setNewThumbnailContributorInfo', this.setNewThumbnailInfo);
  }

  setNewThumbnailInfo = () => {
    this.setupContribInfoTooltips();
  }

  setupContribInfoTooltips() {
    this.pageGrids = document.querySelectorAll('[data-controller*="resources-grid"]:not(.ez-resource-grid--partner)');
    const pageWrapper = document.querySelector('.ez-page-wrapper');

    if (this.contributorsIds) {
      if (!this.hasIdCollectionTarget || this.idCollectionTarget.dataset.featuredGrid === '0') {
        ajaxGet('/async_contributors_info',
          (response) => {
            this.contributorsInfo = response.data;

            if (this.pageGrids.length >= 1) {
              pageWrapper.setAttribute('data-contributors-info', JSON.stringify(response.data));
            }
          },
          (error) => {
            console.error(error); // eslint-disable-line no-console
          },
          { params: { contributor_ids: this.contributorsIds, preview_count: 3 } });
      }
      this.tooltipsInitialized = false;

      mediaMatcher.addMediaQuery('(min-width: 1024px)', this.initTooltips);

      mediaMatcher.evaluateAll();
      mediaMatcher.addListeners();
    }
  }

  setupFollowingThumbnails() {
    ajaxGet('/async_contributors_info', (response) => {
      this.contributorsInfo = response.data;

      this.gatherfollowedUsers().forEach((followedUser) => {
        const thisPreviewsWrap = document.querySelector(`[data-user-id="${followedUser}"] [data-thumbnail-contributor-info-target="previewsWrap"]`);
        const previewImageSets = this.contributorsInfo[followedUser].previews;

        let previewsHTML = '';

        previewImageSets.forEach((thumb) => {
          const {
            thumb_src,
            thumb_src_2x,
            resource_path,
            resource_title,
          } = thumb;

          previewsHTML += this.makePreviewThumbHTML(thumb_src, thumb_src_2x, resource_path, resource_title);
        });

        thisPreviewsWrap.innerHTML = previewsHTML;
      });
    },
    (error) => {
      console.error(error); // eslint-disable-line no-console
    },
    { params: { contributor_ids: this.gatherfollowedUsers(), preview_count: 3, sort: 'recent' } });
  }

  get contributorsIds() {
    if (!this.hasContribInfoTemplateTarget) return null;

    return JSON.parse(this.contribInfoTemplateTarget.dataset.userIds);
  }

  gatherfollowedUsers() {
    const followedUsers = [];

    this.contribThumbnailsTargets.forEach((followedUser) => {
      followedUsers.push(followedUser.dataset.userId);
    });

    return followedUsers;
  }

  initTooltips = () => {
    if (!this.tooltipsInitialized) {
      const controller = this;
      const thisGridNameTargets = this.element.querySelectorAll('[data-resources-grid-target="nameTarget"]:not(.is-tipped)');
      const loadingSpinnerHtml = '<div class="ez-spinner ez-spinner--sm ez-spinner--color-mono"></div>';

      import('tippy.js').then((tippyImport) => {
        const tippy = tippyImport.default;

        tippy(thisGridNameTargets, {
          content: loadingSpinnerHtml,
          allowHTML: true,
          animation: 'rising',
          interactive: true,
          appendTo: document.body,
          placement: 'bottom-start',
          theme: 'contrib-info',
          delay: [250, null],
          duration: 0,
          onCreate(instance) {
            instance.popper.classList.add('contributor-info-tooltip');
          },
          onShow(instance) {
            controller.tippyInstance = instance;
            controller.showTooltip();

            instance.popper.addEventListener('mouseleave', controller.stopPreviewVideo);
          },
          onHide(instance) {
            controller.tippyInstance = instance;
            controller.hideTooltip();

            instance.popper.removeEventListener('mouseleave', controller.stopPreviewVideo);
          },
        });

        this.tooltipsInitialized = true;
      });

      thisGridNameTargets.forEach((el) => {
        el.classList.add('is-tipped');
      });
    }
  }

  showTooltip() {
    this.contributorsInfoPolling = setInterval(this.injectTooltipContent, 100);

    this.injectTooltipContent();
  }

  hideTooltip() {
    clearInterval(this.contributorsInfoPolling);

    const tooltipParentLi = this.getParentLi(this.tippyInstance.reference);
    const tooltipProfileInfo = this.getTooltipHeader(this.tippyInstance.popper);

    tooltipParentLi.classList.remove('is-active');
    if (tooltipProfileInfo) tooltipProfileInfo.removeEventListener('click', this.clickThroughToProfile);
  }

  stopPreviewVideo = (event) => {
    const tooltipParentLi = this.getParentLi(this.tippyInstance.reference);

    if (!this.newTargetIsPreview(event.relatedTarget)) {
      tooltipParentLi.classList.remove('is-playing');
    }
  }

  newTargetIsPreview(newTarget) {
    if (!newTarget.hasAttribute('data-video-preview-target')) return false;
    if (newTarget.getAttribute('data-video-preview-target') !== 'videoWrap') return false;

    return true;
  }

  injectTooltipContent = () => {
    if (this.contributorsInfo || this.pageGrids.length > 1) {
      clearInterval(this.contributorsInfoPolling);

      const templateHTML = this.contribInfoTemplateTarget.innerHTML;
      const parsedContent = this.parseTemplateForContributor(templateHTML);

      setTimeout(() => { // wait a tick to let the parsing happen
        this.initTooltipContent(parsedContent);
      }, 0);
    }
  }

  initTooltipContent(parsedContent) {
    const tooltipContent = this.getTooltipContent(this.tippyInstance.popper);
    const tooltipParentLi = this.getParentLi(this.tippyInstance.reference);

    tooltipContent.innerHTML = parsedContent;

    this.fixAvatar();
    this.loadPreviewThumbs();

    tooltipParentLi.classList.add('is-active');

    const tooltipProfileInfo = this.getTooltipHeader(this.tippyInstance.popper);
    tooltipProfileInfo.addEventListener('click', this.clickThroughToProfile);
  }

  parseTemplateForContributor(templateHTML) {
    // parseTemplate replaces data-href with `url` on item object
    const contribData = this.contributorInfoById;
    contribData.url = contribData.contributor_uploads_path;
    contribData.src = contribData.contributor_avatar_src;

    return parseTemplate(templateHTML, contribData);
  }

  fixAvatar() {
    const tooltipContent = this.getTooltipContent(this.tippyInstance.popper);
    const avatarWrapper = tooltipContent.querySelector('.ez-avatar');
    const avatarTemplate = tooltipContent.querySelector('[data-thumbnail-contributor-info-target="avatarTemplate"]');

    if (this.contributorInfoById.contributor_avatar_src) {
      avatarWrapper.classList.remove('ez-avatar--missing');
      avatarWrapper.innerHTML = avatarTemplate.innerHTML;
    }
  }

  loadPreviewThumbs() {
    const thisPreviews = this.contributorInfoById.previews;
    const thisPreviewsWrap = this.tippyInstance.popper.querySelector('[data-thumbnail-contributor-info-target="previewsWrap"]');

    let previewsHTML = '';

    thisPreviews.forEach((thumb) => {
      const {
        thumb_src: thumbSrc,
        thumb_src_2x: thumbSrc2x,
        resource_path: thumbResourcePath,
        resource_title: thumbResourceTitle,
        pro,
        content_type: contentType,
      } = thumb;

      previewsHTML += this.makePreviewThumbHTML(
        thumbSrc,
        thumbSrc2x,
        thumbResourcePath,
        thumbResourceTitle,
        contentType,
        pro,
      );
    });

    thisPreviewsWrap.innerHTML = previewsHTML;
  }

  makePreviewThumbHTML(src, src2x, resourcePath, resourceTitle, contentType, pro) {
    return `<a href="${resourcePath}"
               class="ez-resource-grid__item__contrib-info__uploads__thumb"
               data-content-type="${contentType}"
               data-pro="${pro}"
               data-action="click->thumbnail-contributor-info#trackResourceClick">
              <img src="${src}"
                   srcset="${src2x} 2x, ${src} 1x" alt="${resourceTitle}"></a>
              `;
  }

  get contributorInfoById() {
    const pageWrapper = document.querySelector('.ez-page-wrapper');
    const contribData = pageWrapper && pageWrapper.hasAttribute('data-contributors-info')
      ? JSON.parse(pageWrapper.getAttribute('data-contributors-info'))
      : {};

    const contributorId = this.tippyInstance.reference.getAttribute('data-user-id');

    if (this.contributorsInfo?.[contributorId] && contribData[contributorId]) {
      const holdUploadsPath = contribData[contributorId].contributor_uploads_path;

      this.contributorsInfo[contributorId] = contribData[contributorId];
      this.contributorsInfo[contributorId].url = holdUploadsPath;
    }

    const dataSource = { ...contribData, ...(this.contributorsInfo || {}) };

    return dataSource[contributorId];
  }

  getParentLi(elem) {
    return elem.closest('.ez-resource-thumb');
  }

  trackContributorClick(event) {
    if (!event) return;

    sendTrackingEvent({
      element: event.target,
      data: {
        action: CONTRIBUTOR_CLICK,
      },
    });
  }

  trackResourceClick(event) {
    const { contentType, pro } = event.target.dataset;

    sendTrackingEvent({
      element: event.target,
      data: {
        action: CONTENT_SELECTION_CLICK,
        label: parseBoolean(pro) ? 'Pro' : 'Free',
        content_type: `Content-${contentType}`,
      },
    });
  }

  clickThroughToProfile = () => {
    this.trackContributorClick();
  }

  getTooltipHeader(tooltip) {
    return tooltip.querySelector('.tippy-box[data-theme=contrib-info] .contributor-details__contributor');
  }

  getTooltipContent(tooltip) {
    return tooltip.querySelector('.tippy-content');
  }

  setupMediaQueries() {
    mediaMatcher.addMediaQuery('(min-width: 1024px)',
      () => { if (this.contributorsIds.length) this.setupContribInfoTooltips(); });
  }
}
